/**
 * CommunityEnablers — Displays key community leader role-slot cards
 * on the Alignment tab, between Mission/Vision/Values and Priority Projects.
 *
 * Follows the ProjectRow visual pattern: section title, capped card grid,
 * and "See all N community leaders →" overflow link.
 *
 * Card rendering delegated to the shared TeamMemberCard component.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  hydrateEnablersFromApi,
  getEnablersForCommunity,
  type CommunityEnabler,
} from "../../Data/CommunityConfig/communityEnablers";
import FlipTeamMemberCard from "../TeamMemberCard/FlipTeamMemberCard";
import {
  fetchCommunityMemberGrants,
  groupGrantsByUser,
  pickBadge,
  type CommunityMemberGrant,
} from "../../api/communityMemberGrants";
import styles from "./CommunityEnablers.module.css";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  communityGuid: string;
  /** Whether the current user can edit (shows vacant slots). */
  canEdit?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Role label resolution                                              */
/*                                                                     */
/*  Post-migration 170: display_title is now the single source of      */
/*  truth, freeform per slot. No i18n catalog of hardcoded roles to    */
/*  fall back to. If display_title is somehow empty, render "Team      */
/*  Member" rather than blank.                                          */
/* ------------------------------------------------------------------ */

function getDisplayTitle(enabler: CommunityEnabler): string {
  const t = (enabler.display_title || "").trim();
  return t || "Team Member";
}

/* ------------------------------------------------------------------ */
/*  Constants matching CSS grid layout                                 */
/* ------------------------------------------------------------------ */

/** Matches grid-template-columns: minmax(MIN_CARD_W, 1fr) in the CSS */
const MIN_CARD_W = 180;
/** Matches gap in the CSS */
const GRID_GAP = 20;
/** Absolute ceiling — never show more than this in the inline row */
const MAX_INLINE = 5;

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const CommunityEnablers: React.FC<Props> = ({
  communityGuid,
  canEdit = false,
}) => {
  const { t } = useAppTranslation("pages");
  const containerRef = useRef<HTMLDivElement>(null);

  // Public-facing surface — own snapshot of enablers, refreshed only
  // from API (on mount and after the admin saves via the Settings
  // tab). Deliberately NOT listening to the in-store mutation event
  // (`l4e_community_enablers_changed`): admin drag-reorder or inline
  // edits would otherwise leak immediately into the public Alignment
  // tab before the admin clicks Save. See communityEnablers.ts event
  // docs for the _changed vs _persisted distinction.
  const [snapshot, setSnapshot] = useState<CommunityEnabler[]>(() =>
    getEnablersForCommunity(communityGuid),
  );

  const refreshFromServer = useCallback(async () => {
    await hydrateEnablersFromApi(communityGuid, true);
    setSnapshot(getEnablersForCommunity(communityGuid));
  }, [communityGuid]);

  useEffect(() => {
    void refreshFromServer();
    const onPersisted = (e: Event) => {
      const ce = e as CustomEvent<{ communityGuid?: string }>;
      if (ce.detail?.communityGuid?.toUpperCase() !== communityGuid.toUpperCase()) return;
      void refreshFromServer();
    };
    window.addEventListener("l4e_community_enablers_persisted", onPersisted);
    return () => window.removeEventListener("l4e_community_enablers_persisted", onPersisted);
  }, [communityGuid, refreshFromServer]);

  const enablers = useMemo(
    () =>
      // Hidden leaders are admin-only — never render on the public surface.
      // Migration 171; controlled from the Settings Leaders edit tray.
      [...snapshot]
        .filter((e) => !e.is_hidden)
        .sort((a, b) => a.sort_order - b.sort_order),
    [snapshot],
  );

  // ── Permission badge auto-computation (POLICY §4.3) ──
  // Fetch active V2 community-scope grants for this community and group
  // by user. The display rule in pickBadge selects the highest-tier
  // bundle per user. Renders inline on the FlipTeamMemberCard roleTitle.
  const [grantsByUser, setGrantsByUser] = useState<Map<string, CommunityMemberGrant[]>>(new Map());
  useEffect(() => {
    let cancelled = false;
    fetchCommunityMemberGrants(communityGuid)
      .then((rows) => {
        if (cancelled) return;
        setGrantsByUser(groupGrantsByUser(rows));
      })
      .catch((err) => {
        // Soft fail — badge is enrichment, not core. Log and move on.
        console.warn("[CommunityEnablers] member-grants fetch failed:", err);
      });
    return () => { cancelled = true; };
  }, [communityGuid]);

  // Filter out vacant slots for public display (admins see them)
  const displaySlots = useMemo(
    () => canEdit ? enablers : enablers.filter((e) => e.manual_name || e.linked_user_guid),
    [enablers, canEdit]
  );

  // ── Calculate how many cards fit in a single row ──────────────────
  const calcFits = useCallback(() => {
    const el = containerRef.current;
    if (!el) return MAX_INLINE;
    const w = el.clientWidth;
    const cols = Math.max(1, Math.floor((w + GRID_GAP) / (MIN_CARD_W + GRID_GAP)));
    return Math.min(cols, MAX_INLINE);
  }, []);

  const [fitsInRow, setFitsInRow] = useState(MAX_INLINE);
  const [showAll, setShowAll] = useState(false);

  // Measure on mount and whenever the container resizes
  useLayoutEffect(() => {
    setFitsInRow(calcFits());
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setFitsInRow(calcFits()));
    ro.observe(el);
    return () => ro.disconnect();
  }, [calcFits, displaySlots.length]);

  // Don't render section if nothing to show
  if (displaySlots.length === 0) return null;

  const visibleCount = Math.min(fitsInRow, displaySlots.length);
  const visible = showAll ? displaySlots : displaySlots.slice(0, visibleCount);
  const hiddenCount = displaySlots.length - visibleCount;

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.header}>
        <span className={styles.title}>
          {t("communityDetail.enablers.sectionTitle")}
        </span>
      </div>

      <div className={showAll ? styles.gridWrap : styles.grid}>
        {visible.map((enabler) => {
          const isVacant = !enabler.linked_user_guid && !enabler.manual_name;
          // POLICY §4.3 — V2 community permission bundle surfaces on the
          // BACK face only. Public front face stays clean (photo + name +
          // position), no platform-role text crowding a person's portrait.
          const backFaceBadge = (() => {
            if (!enabler.linked_user_guid) return null;
            const userKey = String(enabler.linked_user_guid).toUpperCase();
            const userGrants = grantsByUser.get(userKey);
            if (!userGrants || userGrants.length === 0) return null;
            return pickBadge(userGrants)?.label ?? null;
          })();
          return (
            <FlipTeamMemberCard
              key={enabler.role_slot_guid}
              displayName={enabler.manual_name || ""}
              photoUrl={enabler.manual_photo_url}
              roleTitle={getDisplayTitle(enabler)}
              linkedUserGuid={enabler.linked_user_guid}
              userGuid={enabler.linked_user_guid}
              contactEmail={enabler.manual_email}
              contactProfileUrl={enabler.external_profile_url}
              backFaceBadge={backFaceBadge}
              isVacant={isVacant}
              avatarVariant={
                isVacant ? 'default'
                : enabler.linked_user_guid ? 'l4e'
                : 'expanded'
              }
              splitName
              size="lg"
            />
          );
        })}
      </div>

      {hiddenCount > 0 && !showAll && (
        <button
          type="button"
          className={styles.viewAllBtn}
          onClick={() => setShowAll(true)}
        >
          {t("communityDetail.enablers.viewAll", { count: displaySlots.length })}
          {" \u2192"}
        </button>
      )}

      {showAll && hiddenCount > 0 && (
        <button
          type="button"
          className={styles.viewAllBtn}
          onClick={() => setShowAll(false)}
        >
          {t("communityDetail.enablers.showLess")}
        </button>
      )}
    </div>
  );
};

export default CommunityEnablers;
